import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface UserAllocation {
  userId: string;
  displayName: string;
  email: string;
  role: string;
}

interface CreditTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creditType: "ai" | "lead";
}

const WORKSPACE_SENTINEL = "__workspace__";

export function CreditTransferDialog({ open, onOpenChange, creditType }: CreditTransferDialogProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [fromId, setFromId] = useState<string>("");
  const [toId, setToId] = useState<string>("");
  const [amount, setAmount] = useState<string>("");

  const { data: users = [], isLoading: loadingUsers } = useQuery<UserAllocation[]>({
    queryKey: ["/api/admin/credit-config/users"],
    enabled: open,
  });

  const unit = creditType === "ai" ? "cr" : "credits";

  const transferMutation = useMutation({
    mutationFn: async () => {
      const fromUserId = fromId === WORKSPACE_SENTINEL ? null : fromId;
      const toUserId = toId === WORKSPACE_SENTINEL ? null : toId;
      const res = await apiRequest("POST", "/api/admin/credit-config/transfer", {
        creditType,
        fromUserId,
        toUserId,
        amount: parseInt(amount),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || "Transfer failed");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/credit-config/users"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/credit-config"] });
      qc.invalidateQueries({ queryKey: ["/api/ai-credits/balance"] });
      qc.invalidateQueries({ queryKey: ["/api/lead-credits/balance"] });
      toast({ title: "Credits transferred", description: `${parseInt(amount).toLocaleString()} ${unit} moved successfully.` });
      onOpenChange(false);
      setFromId("");
      setToId("");
      setAmount("");
    },
    onError: (e: any) => {
      toast({ title: "Transfer failed", description: e.message, variant: "destructive" });
    },
  });

  const fromLabel = (id: string) => {
    if (id === WORKSPACE_SENTINEL) return "Workspace pool";
    return users.find((u) => u.userId === id)?.displayName ?? id;
  };

  const toOptions = fromId
    ? [{ value: WORKSPACE_SENTINEL, label: "Workspace pool" }, ...users.map((u) => ({ value: u.userId, label: u.displayName }))]
        .filter((o) => o.value !== fromId)
    : [];

  const canSubmit =
    fromId &&
    toId &&
    fromId !== toId &&
    parseInt(amount) > 0 &&
    !isNaN(parseInt(amount)) &&
    !transferMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-credit-transfer">
        <DialogHeader>
          <DialogTitle>Transfer {creditType === "ai" ? "AI" : "Lead"} credits</DialogTitle>
          <DialogDescription>
            Move credits between the workspace pool and individual users, or between users.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {loadingUsers ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>From</Label>
                <Select value={fromId} onValueChange={(v) => { setFromId(v); setToId(""); }} data-testid="select-transfer-from">
                  <SelectTrigger>
                    <SelectValue placeholder="Choose source…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={WORKSPACE_SENTINEL}>Workspace pool</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.userId} value={u.userId}>
                        {u.displayName}
                        <span className="ml-1 text-muted-foreground text-xs">({u.email})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-center">
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </div>

              <div className="space-y-2">
                <Label>To</Label>
                <Select
                  value={toId}
                  onValueChange={setToId}
                  disabled={!fromId}
                  data-testid="select-transfer-to"
                >
                  <SelectTrigger>
                    <SelectValue placeholder={fromId ? "Choose destination…" : "Select source first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {toOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Amount ({unit})</Label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  placeholder="e.g. 500"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="input-transfer-amount"
                />
              </div>

              {fromId && toId && parseInt(amount) > 0 && (
                <p className="text-sm text-muted-foreground">
                  Transfer <strong>{parseInt(amount).toLocaleString()} {unit}</strong> from{" "}
                  <strong>{fromLabel(fromId)}</strong> → <strong>{fromLabel(toId)}</strong>
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => transferMutation.mutate()}
            disabled={!canSubmit}
            data-testid="button-confirm-transfer"
          >
            {transferMutation.isPending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Transferring…</>
            ) : "Transfer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
